<template>
  <div class="member-center">
    <!-- Header -->
    <header class="member-center-header">
      <div class="logo-container">
        <img
          src="~@/assets/img/photos/product_logo.png"
          alt="商標"
          class="logo"
        />
      </div>
    </header>

    <h1 class="title">會員專區</h1>

    <!-- 未登入：顯示登入表單 -->
    <div v-if="!isLoggedIn" class="login-section">
      <div class="form-group">
        <label for="phone">手機號碼</label>
        <input
          type="tel"
          id="phone"
          v-model="loginForm.phone"
          placeholder="請輸入手機號碼"
        />
      </div>
      <div class="form-group">
        <label for="password">密碼</label>
        <input
          type="password"
          id="password"
          v-model="loginForm.password"
          placeholder="請輸入密碼"
          @keyup.enter="handleLogin"
        />
      </div>
      <button @click="handleLogin" :disabled="isLoading">
        {{ isLoading ? '登入中...' : '登入' }}
      </button>
      <button class="cancel-btn" @click="goBack">取消</button>
      <p v-if="loginError" class="error-message">{{ loginError }}</p>
    </div>

    <!-- 已登入：顯示會員資訊與訂閱列表 -->
    <template v-else>
      <!-- 會員基本資料 -->
      <div class="member-info-section">
        <div class="info-group">
          <label>姓名</label>
          <div class="info-value">{{ member.name }}</div>
        </div>
        <div class="info-group">
          <label>生日</label>
          <div class="info-value">{{ member.birthday }}</div>
        </div>
        <div class="info-group">
          <label>手機</label>
          <div class="info-value">{{ member.phone }}</div>
        </div>
        <div class="info-group">
          <label>Email</label>
          <div class="info-value">{{ member.email || '-' }}</div>
        </div>
        <div class="info-group">
          <label>會員等級</label>
          <div class="info-value">{{ member.level_name || '一般' }}</div>
        </div>
      </div>

      <!-- 訂閱中方案列表 -->
      <div class="subscription-section">
        <h2 class="section-title">訂閱中方案</h2>

        <div v-if="subscriptions.length === 0" class="no-subscriptions">
          目前沒有訂閱中的方案
        </div>

        <div v-else class="subscription-list">
          <div
            v-for="subscription in subscriptions"
            :key="subscription.id"
            class="subscription-item"
          >
            <div class="subscription-row">
              <span class="program-name">{{ subscription.program_name }}</span>
              <span v-if="subscription.failed_billing" class="failed-tag">
                扣款失敗
              </span>
              <button
                v-if="subscription.can_update_card"
                class="update-card-btn"
                @click="openUpdateCardModal(subscription)"
              >
                更新付款卡片
              </button>
              <button
                class="cancel-btn"
                :disabled="!subscription.can_cancel"
                @click="confirmCancel(subscription)"
              >
                取消訂閱
              </button>
            </div>
            <p v-if="subscription.failed_billing" class="failed-notice">
              {{ formatFailedNotice(subscription) }}
            </p>
            <!-- 暫停中但尚無失敗扣款資料（扣款結果待確認）：僅顯示灰字提示，不提供換卡 -->
            <p
              v-else-if="isBillingProcessing(subscription)"
              class="processing-notice"
            >
              扣款處理中，請稍後再查看。
            </p>
          </div>
        </div>
      </div>

      <!-- 返回按鈕 -->
      <div class="back-section">
        <button class="back-btn" @click="goBack">返回首頁</button>
      </div>
    </template>

    <!-- 取消訂閱確認對話框 -->
    <div v-if="showConfirmModal" class="confirm-modal">
      <div class="modal-content">
        <p>
          確定要取消「{{ selectedSubscription.program_name }}」的訂閱嗎？
          取消後將不再自動續訂扣款。
        </p>
        <div class="modal-buttons">
          <button class="cancel-modal-btn" @click="closeConfirmModal">
            取消
          </button>
          <button
            class="confirm-btn"
            @click="handleCancelSubscription"
            :disabled="isCancelling"
          >
            {{ isCancelling ? '處理中...' : '確定取消' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 更新付款卡片確認對話框 -->
    <div v-if="showUpdateCardModal" class="confirm-modal">
      <div class="modal-content">
        <p>
          將以新的信用卡支付本期費用 NT${{
            formatAmount(updateCardTarget.failed_billing.amount)
          }}，付款成功後，之後的每期費用都會從這張卡扣款。
        </p>
        <div class="modal-buttons">
          <button
            class="cancel-modal-btn"
            @click="closeUpdateCardModal"
            :disabled="isUpdatingCard"
          >
            取消
          </button>
          <button
            class="pay-btn"
            @click="handleUpdateCard"
            :disabled="isUpdatingCard"
          >
            {{ isUpdatingCard ? '處理中...' : '前往付款' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { login } from '@/api/shopping_auth';
import moment from 'moment';
import {
  getSubscriptions,
  cancelSubscription,
  startCardUpdate
} from '@/api/member_center';

export default {
  name: 'MemberCenter',
  data() {
    return {
      // 登入相關
      isLoggedIn: false,
      isLoading: false,
      loginError: '',
      loginForm: {
        phone: '',
        password: ''
      },
      // 會員資料
      member: {
        id: null,
        name: '',
        birthday: '',
        phone: '',
        email: '',
        level_name: ''
      },
      // 訂閱列表
      subscriptions: [],
      // 取消訂閱相關
      showConfirmModal: false,
      selectedSubscription: null,
      isCancelling: false,
      // 更新付款卡片相關
      showUpdateCardModal: false,
      updateCardTarget: null,
      isUpdatingCard: false
    };
  },
  methods: {
    /**
     * 處理登入
     */
    handleLogin() {
      if (!this.loginForm.phone || !this.loginForm.password) {
        this.loginError = '請輸入手機號碼和密碼';
        return;
      }

      this.isLoading = true;
      this.loginError = '';

      login(
        {
          phone: this.loginForm.phone,
          password: this.loginForm.password
        },
        response => {
          this.isLoading = false;
          // 登入成功，儲存會員資料
          // 注意：API 返回的是 member_id 不是 id
          this.member = {
            id: response.member_id,
            name: response.name,
            birthday: response.birthday,
            phone: response.phone,
            email: response.email,
            level_name: response.level_name || '一般'
          };
          this.isLoggedIn = true;
          // 取得訂閱列表
          this.fetchSubscriptions();
        },
        () => {
          this.isLoading = false;
          this.loginError = '登入失敗，請確認帳號密碼是否正確';
        }
      );
    },

    /**
     * 取得會員訂閱列表
     */
    fetchSubscriptions() {
      if (!this.member.id) return;

      getSubscriptions(
        { member_id: this.member.id },
        response => {
          this.subscriptions = response.subscriptions || [];
        },
        () => {
          console.error('取得訂閱列表失敗');
        }
      );
    },

    /**
     * 開啟取消訂閱確認對話框
     */
    confirmCancel(subscription) {
      this.selectedSubscription = subscription;
      this.showConfirmModal = true;
    },

    /**
     * 關閉取消訂閱確認對話框
     */
    closeConfirmModal() {
      this.showConfirmModal = false;
      this.selectedSubscription = null;
    },

    /**
     * 處理取消訂閱
     */
    handleCancelSubscription() {
      if (!this.selectedSubscription) return;

      this.isCancelling = true;

      cancelSubscription(
        this.selectedSubscription.id,
        this.member.id,
        () => {
          this.isCancelling = false;
          this.closeConfirmModal();
          // 重新取得訂閱列表
          this.fetchSubscriptions();
          alert('訂閱已取消');
        },
        errorMessage => {
          this.isCancelling = false;
          alert(errorMessage || '取消訂閱失敗，請稍後再試');
        }
      );
    },

    /**
     * 金額加上千分位
     * @param {number} amount - 金額
     * @returns {string} 千分位格式字串
     */
    formatAmount(amount) {
      return Number(amount).toLocaleString('en-US');
    },

    /**
     * 是否為「扣款處理中」：訂閱暫停（status 3）但沒有失敗扣款資料
     * @param {Object} subscription - 訂閱資料
     * @returns {boolean} 是否顯示扣款處理中提示
     */
    isBillingProcessing(subscription) {
      return Number(subscription.status) === 3 && !subscription.failed_billing;
    },

    /**
     * 組合扣款失敗說明文字
     * @param {Object} subscription - 訂閱資料（含 failed_billing）
     * @returns {string} 說明文字
     */
    formatFailedNotice(subscription) {
      const billing = subscription.failed_billing;
      let text =
        `${moment(billing.billing_date).format('MM/DD')} 扣款 ` +
        `NT$${this.formatAmount(billing.amount)} 未成功`;

      // 有卡號末四碼才顯示，避免出現「末四碼 null」
      if (subscription.card_last_four) {
        text += `（卡號末四碼 ${subscription.card_last_four}）`;
      }
      text += '。';

      // 尚有下次重試時間才顯示自動重試說明
      if (billing.next_retry_at) {
        text +=
          `系統將於 ${moment(billing.next_retry_at).format(
            'MM/DD'
          )} 自動重試，` + `剩餘 ${billing.remaining_retries} 次。`;
      }

      return text + '若您已更換信用卡，請更新付款卡片。';
    },

    /**
     * 開啟更新付款卡片確認對話框
     * @param {Object} subscription - 要更新卡片的訂閱
     */
    openUpdateCardModal(subscription) {
      this.updateCardTarget = subscription;
      this.showUpdateCardModal = true;
    },

    /**
     * 關閉更新付款卡片確認對話框
     */
    closeUpdateCardModal() {
      this.showUpdateCardModal = false;
      this.updateCardTarget = null;
    },

    /**
     * 處理更新付款卡片：建立付款後以 token 導向金流頁
     */
    handleUpdateCard() {
      if (!this.updateCardTarget || this.isUpdatingCard) return;

      this.isUpdatingCard = true;

      startCardUpdate(
        this.updateCardTarget.id,
        this.member.id,
        response => {
          // 導向金流頁，離開頁面前維持「處理中」狀態避免重複點擊
          window.location.href = `${process.env.VUE_APP_API_URL}/shopping/subscription/redirect-to-card-update?token=${response.redirect_token}`;
        },
        errorMessage => {
          this.isUpdatingCard = false;
          alert(errorMessage || '更新付款卡片失敗，請稍後再試');
        }
      );
    },

    /**
     * 返回首頁（方案列表頁）
     */
    goBack() {
      this.$router.push({ name: 'ProductPlan' });
    }
  },
  mounted() {
    document.title = '昱驊養生瑜珈中心-會員專區';
  }
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/page/MemberCenter.scss';
</style>
